import React, { useState, useEffect, useCallback } from 'react';
import { X, DownloadSimple } from '@phosphor-icons/react';
import { Button } from './ui/button';

export default function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setInstalled(true);
      return;
    }

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const handleAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setInstalled(true);
    }
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const handleDismiss = () => {
    setDismissed(true);
  };

  // iOS Safari detection - doesn't support beforeinstallprompt
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isIOSSafari = isIOS && isSafari;
  const isInStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

  if (installed || isInStandalone || dismissed) return null;

  // iOS Safari: show manual instructions
  if (isIOSSafari) {
    return (
      <div data-testid="pwa-install-banner" className="bg-gradient-to-r from-[#6B4D5F] to-[#8B6D7F] text-white px-4 py-3 flex items-center justify-between gap-3 shadow-lg z-50">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
            <DownloadSimple size={20} weight="bold" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold leading-tight">Install Cut2Pro</p>
            <p className="text-[11px] text-white/80 leading-tight">Tap the Share button, then "Add to Home Screen"</p>
          </div>
        </div>
        <button onClick={handleDismiss} className="p-1.5 rounded-lg hover:bg-white/10 shrink-0">
          <X size={16} />
        </button>
      </div>
    );
  }

  // Chrome/Edge/Android: show install button if prompt is available
  if (!deferredPrompt) return null;

  return (
    <div data-testid="pwa-install-banner" className="bg-gradient-to-r from-[#6B4D5F] to-[#8B6D7F] text-white px-4 py-3 flex items-center justify-between gap-3 shadow-lg z-50">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
          <DownloadSimple size={20} weight="bold" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold leading-tight">Install Cut2Pro</p>
          <p className="text-[11px] text-white/80 leading-tight">Get the full app experience on your device</p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          data-testid="pwa-install-btn"
          onClick={handleInstall}
          size="sm"
          className="bg-white text-[#6B4D5F] hover:bg-white/90 font-bold text-xs h-8 px-4 shadow-md"
        >
          <DownloadSimple size={14} className="mr-1" weight="bold" /> Install
        </Button>
        <button onClick={handleDismiss} className="p-1.5 rounded-lg hover:bg-white/10">
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
